<script setup lang="ts">
import type { InlineEditableField } from '@/components/finest-field-grid/types'
import FinestListItem from '@/components/FinestListItem.vue'
import FinestMasterDetailCard from '@/components/FinestMasterDetailCard.vue'
import FinestMasterListGroup from '@/components/FinestMasterListGroup.vue'
import HouseholdMemberFields from '@/pages/investors/profile/HouseholdMemberFields.vue'
import type { ExtendedEnum } from '@/types'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SPOUSE_KEY, useHouseholdMembers, type ChildData, type SpouseData } from './useHouseholdMembers'
import NaturalPersonData = App.Data.User.NaturalPersonData

const props = defineProps<{
  person: NaturalPersonData
  spouse?: SpouseData | null
  children?: ChildData[]
  genderOptions: ExtendedEnum[]
}>()

const { t } = useI18n()

const { children, selectedKey, selectedChildIndex, showsSpouse, addSpouse, addChild, removeMember } =
  useHouseholdMembers(props)

const isMarried = computed(
  () => props.person.marital_status === 'married' || props.person.marital_status === 'registered_civil_partnership',
)

const spouseFields = computed<InlineEditableField[]>(() => [
  { key: 'first_name', label: t('fields.first_name'), type: 'text' },
  { key: 'last_name', label: t('fields.last_name'), type: 'text' },
  {
    key: 'gender',
    label: t('fields.gender'),
    type: 'select',
    options: props.genderOptions.map((option) => ({ value: option.id, label: t(`gender.${option.id}`) })),
  },
  { key: 'birth_date', label: t('fields.birth_date'), type: 'date' },
  {
    key: 'has_community_of_accrued_gains',
    label: t('fields.has_community_of_accrued_gains'),
    type: 'boolean',
  },
  { key: 'has_separate_assessment', label: t('fields.has_separate_assessment'), type: 'boolean' },
])

const childFields = computed<InlineEditableField[]>(() => [
  { key: 'first_name', label: t('fields.first_name'), type: 'text' },
  { key: 'last_name', label: t('fields.last_name'), type: 'text' },
  { key: 'birth_date', label: t('fields.birth_date'), type: 'date' },
  { key: 'living_in_household', label: t('fields.living_in_household'), type: 'boolean' },
  { key: 'receives_child_support', label: t('fields.receives_child_support'), type: 'boolean' },
])

const displayName = (member: NaturalPersonData, fallback: string): string =>
  [member.first_name, member.last_name].filter(Boolean).join(' ') || fallback
</script>

<template>
  <FinestMasterDetailCard>
    <template #master>
      <FinestMasterListGroup :label="t('groups.spouse')" icon="pi pi-heart" class="mb-5">
        <FinestListItem
          v-if="spouse"
          :label="displayName(spouse, t('groups.spouse'))"
          :selected="showsSpouse"
          @click="selectedKey = SPOUSE_KEY" />

        <button
          v-else-if="isMarried"
          type="button"
          class="border-line text-ink-3 hover:border-action hover:text-action cursor-pointer rounded-[10px] border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
          @click="addSpouse">
          <i class="pi pi-plus mr-1.5 text-xs" aria-hidden="true" />
          {{ t('actions.addSpouse') }}
        </button>

        <p v-else class="text-ink-3 px-0.5 text-xs">{{ t('hints.maritalStatus') }}</p>
      </FinestMasterListGroup>

      <FinestMasterListGroup :label="t('groups.children')" icon="pi pi-users">
        <FinestListItem
          v-for="child in children"
          :key="child.id"
          :label="displayName(child, t('groups.child'))"
          :selected="child.id === selectedKey"
          @click="selectedKey = child.id ?? null" />

        <button
          type="button"
          class="border-line text-ink-3 hover:border-action hover:text-action cursor-pointer rounded-[10px] border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
          @click="addChild">
          <i class="pi pi-plus mr-1.5 text-xs" aria-hidden="true" />
          {{ t('actions.addChild') }}
        </button>
      </FinestMasterListGroup>
    </template>

    <template #detail>
      <div v-if="showsSpouse">
        <div class="flex items-center justify-between gap-4 pb-4">
          <h2>{{ displayName(spouse!, t('groups.spouse')) }}</h2>
          <Button
            :label="t('actions.remove')"
            icon="pi pi-trash"
            severity="danger"
            text
            size="small"
            @click="removeMember(spouse?.id)" />
        </div>

        <HouseholdMemberFields :key="spouse!.id" :member="spouse!" :fields="spouseFields" />
      </div>

      <div v-else-if="selectedChildIndex !== -1">
        <div class="flex items-center justify-between gap-4 pb-4">
          <h2>{{ displayName(children[selectedChildIndex], t('groups.child')) }}</h2>
          <Button
            :label="t('actions.remove')"
            icon="pi pi-trash"
            severity="danger"
            text
            size="small"
            @click="removeMember(children[selectedChildIndex]?.id)" />
        </div>

        <HouseholdMemberFields
          :key="children[selectedChildIndex].id"
          :member="children[selectedChildIndex]"
          :fields="childFields" />
      </div>

      <p v-else class="text-ink-3 py-12 text-center text-sm">{{ t('hints.empty') }}</p>
    </template>
  </FinestMasterDetailCard>
</template>

<i18n>
{
  "de": {
    "groups": {
      "spouse": "Ehepartner:in",
      "children": "Kinder",
      "child": "Kind"
    },
    "actions": {
      "addSpouse": "Ehepartner:in hinzufügen",
      "addChild": "Kind hinzufügen",
      "remove": "Entfernen"
    },
    "hints": {
      "maritalStatus": "Ein:e Ehepartner:in ist nur bei verheiratet oder eingetragener Lebenspartnerschaft möglich.",
      "empty": "Wähle links ein Familienmitglied aus oder lege eines an."
    },
    "fields": {
      "first_name": "Vorname",
      "last_name": "Nachname",
      "gender": "Geschlecht",
      "birth_date": "Geburtsdatum",
      "has_community_of_accrued_gains": "Zugewinngemeinschaft",
      "has_separate_assessment": "Getrennte Veranlagung",
      "living_in_household": "Lebt im Haushalt",
      "receives_child_support": "Erhält Unterhalt"
    },
    "gender": {
      "male": "Männlich",
      "female": "Weiblich",
      "diverse": "Divers"
    }
  },
  "en": {
    "groups": {
      "spouse": "Spouse",
      "children": "Children",
      "child": "Child"
    },
    "actions": {
      "addSpouse": "Add spouse",
      "addChild": "Add child",
      "remove": "Remove"
    },
    "hints": {
      "maritalStatus": "A spouse is only possible for married or registered civil partnership.",
      "empty": "Select a family member on the left or create one."
    },
    "fields": {
      "first_name": "First name",
      "last_name": "Last name",
      "gender": "Gender",
      "birth_date": "Date of birth",
      "has_community_of_accrued_gains": "Community of accrued gains",
      "has_separate_assessment": "Separate tax assessment",
      "living_in_household": "Living in household",
      "receives_child_support": "Receives child support"
    },
    "gender": {
      "male": "Male",
      "female": "Female",
      "diverse": "Diverse"
    }
  }
}
</i18n>
